import watch from "redux-watch";
import {Store} from "redux";

import {hasAnySetsAtPaths} from '../../../util/test-object-condition';
import {settings} from '../../../settings/settings-util';
import toggleCallToActionButtonsToState from "./disablers/callToAction";
import toggleDisplayLinksToState from "./disablers/displayLink";
import toggleDisplayLinkFoldToState from "./disablers/displayLinkFold";
import toggleInlineSurveyButtonToState from "./disablers/inlineSurveyButton";
import toggleInputBoxToState from "./disablers/inputBox";
import toggleInputSubmitButtonToState from "./disablers/inputSubmitButton";
import toggleMinimizeButtonToState from "./disablers/minimizeButton";
import toggleNavigationButtonsToState from "./disablers/navigationLink";
import toggleResponseMessageToState from './disablers/responseMessage';
import toggleSkipToContentButtonToState from "./disablers/skipToContent";
import {InitializationPromises} from '../../../types/initialization';
import {PathCondition} from '../../../settings/settings';
import {AlmeState} from "../../../wireup/AlmeState";
import toggleWindowFormToState from "./disablers/windowForm";
import toggleHeaderButtonsToState from "./disablers/headerButtons";
import toggleAgentAssistButtonsToState from "./disablers/agentAssist";

interface TypedPathCondition {
    condition: PathCondition<unknown>;
    type: string;
}

interface ConditionDictionary {
    [key: string]: TypedPathCondition[];
}

export default class ElementDisabler {
    private _mapFunctions;

    public _store: Store<AlmeState> | null = null;
    public _settings: typeof settings;
    public _initializationPromises: InitializationPromises | null = null;

    //#region Public Interface

    constructor(passedSettings) {
        this._settings = passedSettings || settings;

        this._mapFunctions = {
            agentAssistButtonsDisableWhen: toggleAgentAssistButtonsToState,
            callToActionDisableWhen: toggleCallToActionButtonsToState,
            displayLinkDisableWhen: toggleDisplayLinksToState,
            displayLinkFoldDisableWhen: toggleDisplayLinkFoldToState,
            headerButtonsDisableWhen: toggleHeaderButtonsToState,
            inlineSurveyButtonDisableWhen: toggleInlineSurveyButtonToState,
            inputBoxDisableWhen: toggleInputBoxToState,
            inputSubmitButtonDisableWhen: toggleInputSubmitButtonToState,
            minimizeButtonDisableWhen: toggleMinimizeButtonToState,
            navigationLinksDisableWhen: toggleNavigationButtonsToState,
            responseMessageDisableWhen: toggleResponseMessageToState,
            skipToContentButtonDisableWhen: toggleSkipToContentButtonToState,
            windowFormDisableWhen: toggleWindowFormToState,
        };
    }

    static getDefaultSettings(): typeof settings {
        return settings;
    }

    initialize(store: Store<AlmeState>, initializationPromises: InitializationPromises): void {
        this._store = store;
        this._initializationPromises = initializationPromises;

        this.setupSubscriptions(this._settings.ElementDisabler);

        this.persistDisable();
    }

    setupSubscriptions(conditionDict: typeof settings.ElementDisabler): void {
        const watchDict = {} as ConditionDictionary;

        // Find unique state paths and associate with conditions in dict
        for (const type in conditionDict) {
            const conditions = conditionDict[type] as PathCondition<unknown>[];
            for (const condition of conditions) {
                const { statePath, path } = condition;
                const dictPath = statePath || path;
                if (!dictPath) {
                    continue;
                }
                if (!watchDict[dictPath]) {
                    watchDict[dictPath] = [];
                }
                watchDict[dictPath].push({ condition, type });
            }
        }

        // Set up watch for all conditions on each state path
        for (const path in watchDict) {
            this.subscribeState(path, watchDict[path]);
        }
    }

    subscribeState(path: string, conditions: TypedPathCondition[]): void {
        const getState = this._store?.getState.bind(this);
        const watcher = watch(getState, path);
        this._store?.subscribe(watcher(() => {
            setTimeout(() => {
                // Check all associated conditions and
                // enable/disable on state change
                const pathConditions = conditions.map((value) => {
                    return value.condition;
                });
                for (const { type } of conditions) {
                    const shouldDisable = this.shouldDisable(pathConditions);
                    const handleChange = this._mapFunctions[type];
                    handleChange(this._settings, shouldDisable);
                }
            }, 0);
        }));
    }

    async persistDisable(): Promise<void> {
        await this._initializationPromises?.onLoad;

        const disablerSettings = this._settings.ElementDisabler;

        Object.keys(disablerSettings).forEach(type => {
            disablerSettings[type].forEach(instance => {
                const statePath = instance.statePath.split(".");
                const currentState = this._store?.getState()[statePath[0]][statePath[1]];
                this.disableAfterConversationRendered(currentState, disablerSettings[type], type);
            });
        });
    }

    disableAfterConversationRendered(currentState, conditions: PathCondition<unknown>[], type): void {
        if (this.conversationElementsNotRendered()) {
            setTimeout(() => {
                this.disableAfterConversationRendered(currentState, conditions, type);
            }, 500);
        }
        else {
            this._mapFunctions[type](this._settings, this.shouldDisable(conditions));
        }
    }

    shouldDisable(conditions: PathCondition<unknown>[]): boolean {
        // Note: This assumes that these conditions are being ORed together.
        const convertedConditions = conditions.map((obj) => {
            return [obj];
        });

        return hasAnySetsAtPaths(convertedConditions, this._store?.getState());
    }

    conversationElementsNotRendered(): boolean {
        const conversationArea = document.getElementsByClassName(this._settings.classNames.conversationAreaClassName)[0];
        return !document.getElementById(this._settings.Window.rootId) &&
            (!conversationArea || conversationArea.children.length === 0);
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/element-disabler/src/element-disabler.ts